/**
 * 보호 에셋 DEK 언랩 API — asset-delivery v2.
 *
 * BE endpoint:
 *   POST /api/v1/assets/keys/unwrap  body { hashes: string[] }
 *
 * lease.protection='AES_GCM_V1' 인 파일의 sha256(암호문 기준)을 보내면
 * 활성 릴리스 소속 파일에 한해 DEK(base64)를 돌려준다. 조건 미달 해시는
 * 오류가 아니라 응답에서 빠진다 — 호출부는 부재를 "언랩 불가"로 다룬다.
 *
 * ⚠ **에셋 토큰(aud=asset, RS256)이 있어야 한다** (2026-08-31 배달 게이트, BE
 *   SecurityConfig `hasRole("ASSET")`). 종전엔 permitAll 이라 회원 토큰이 붙든 말든
 *   통과했는데, 그때는 manifest 한 번이면 인식 모델 전량을 익명으로 받을 수 있었다.
 *   ⚠ 회원 세션 토큰(HS384)을 보내면 **403 '권한이 없습니다'** 다 — JwtAuthFilter 가
 *     회원으로는 인증시키지만 ROLE_ASSET 이 서지 않는다. 2026-09-02 기기에서 스캔이
 *     통째로 가짜 엔진으로 폴백한 원인이 이것이었다(게이트만 잠그고 이 호출부가
 *     따라가지 않았다).
 *
 * ⚠ **공유 client 를 쓰지 않는다.** 두 가지가 걸린다:
 *   ① 요청 인터셉터가 Authorization 을 **무조건** 회원 토큰으로 덮어쓴다 — config 로
 *      넘긴 에셋 토큰이 살아남지 못한다.
 *   ② 응답 인터셉터의 401 경로는 silent refresh 를 돌리고 실패 시 `authStore.clear()` —
 *      즉 **에셋 토큰이 상한 것 때문에 회원이 로그아웃될 수 있다.** 남의 도메인 실패가
 *      세션을 건드리게 두지 않는다.
 *   그래서 envelope 해석만 여기서 직접 한다(client.ts 응답 인터셉터와 같은 규칙).
 */
import axios, { type AxiosError } from "axios";
import { apiRootUrl } from "./client";
import { getAssetToken } from "../native/assetToken";
import { ApiError, isApiEnvelope, type ApiEnvelope } from "../lib/errors";
import i18n from "../i18n";

export interface UnwrappedAssetKey {
  hash: string;
  protection: string;
  /** base64 32바이트 DEK — 엔진 engineOptions 로만 전달, 저장 금지 */
  dek: string;
}

export async function unwrapAssetKeys(hashes: string[]): Promise<Map<string, string>> {
  if (hashes.length === 0) return new Map();

  const token = await getAssetToken();
  // 토큰이 없으면 요청해도 401 이다. 여기서 끊어야 호출부(useScanSession)가 lease 를
  // 정리하고 폴백한다 — 빈 Map 을 주면 DEK 없이 엔진이 떠서 모델 로드에서 조용히 깨진다.
  if (!token) throw new ApiError("E401", i18n.t("error.assetToken"));

  let res;
  try {
    // ⚠ client baseURL 은 …/api/v1/user — 상대경로로 넘기면 /user 가 중복된다
    //   (…/user/api/v1/assets/…). user 밖 endpoint 는 apiRootUrl 절대 URL 로 호출한다.
    res = await axios.post<ApiEnvelope<{ keys: UnwrappedAssetKey[] }>>(
      `${apiRootUrl()}/assets/keys/unwrap`,
      { hashes },
      {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        timeout: 10000,
        // 네이티브는 쿠키를 안 쓴다 — CapacitorCookies 가 응답 처리에 끼어들지 않게.
        withCredentials: false,
      },
    );
  } catch (err) {
    // axios 는 비 2xx 에서 먼저 던진다 — envelope 검사까지 오지 않으므로 여기서 옮긴다.
    // 게이트 거부(401/403)가 바로 이 경로다. 로그에 사유가 남아야 다음 사람이 안 헤맨다.
    const e = err as AxiosError<ApiEnvelope>;
    const status = e.response?.status;
    const env = e.response?.data;
    throw new ApiError(
      isApiEnvelope(env) ? env.code : `E${status ?? 500}`,
      isApiEnvelope(env) ? env.message : (e.message ?? i18n.t("error.assetKey")),
      { httpStatus: status },
    );
  }

  const body = res.data;
  if (!isApiEnvelope(body) || !body.code.startsWith("S")) {
    throw new ApiError(
      isApiEnvelope(body) ? body.code : `E${res.status}`,
      isApiEnvelope(body) ? body.message : i18n.t("error.assetKey"),
      { httpStatus: res.status },
    );
  }
  return new Map((body.data?.keys ?? []).map((k) => [k.hash, k.dek]));
}
